import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useController } from 'react-hook-form';

import { Box, ClickAwayListener, FormControl, Popper, Typography } from '@material-ui/core';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import clsx from 'clsx';
import moment from 'moment';

import { CalendarIcon, colors } from '../../../assets';
import { USADateFormat, apiDateFormat } from '../../../utils';
import { HelperTxt } from '../../helperTxt/HelperTxt';
import { Input } from '../../input/Input';
import { Paper } from '../../paper/Paper';
import { CalendarPicker } from '../../pickers';
import { useDatePickerStyles } from './styles';
import { DatePickerProps } from './types';

const today = moment().format(USADateFormat);
const tomorrow = moment().add(1, 'days').format(USADateFormat);

export const FormDatePicker = memo(
  ({ control, disabled, id, label, max, min, name, renderDay, renderNote, required }: DatePickerProps) => {
    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: undefined,
    });

    const customValue = useCallback((date: string) => {
      if (date) {
        const current = moment(date).format(USADateFormat);

        if (today === current) {
          return `Today (${today})`;
        }

        if (tomorrow === current) {
          return `Tomorrow (${tomorrow})`;
        }

        return current;
      }

      return '';
    }, []);

    const [selectVisible, setSelectVisibility] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);

    const pickerBoxRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      setTimeout(() => {
        if (selectVisible && pickerBoxRef.current) {
          pickerBoxRef.current.focus();
        }
      }, 10);
    }, [selectVisible]);

    const styles = useDatePickerStyles({
      dropdownWidth: anchorRef.current?.offsetWidth,
    });

    const handleClose = useCallback((event: React.MouseEvent<EventTarget>) => {
      if (!anchorRef.current || !anchorRef.current.contains(event.target as HTMLElement)) {
        setSelectVisibility(false);
      }
    }, []);

    const pickerToggle = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (!disabled) {
          setSelectVisibility(prevState => !prevState);
        }
      },
      [disabled],
    );

    // The calendar view always passes the clicked day; it never clears the value to null.
    const onChange = useCallback((data: MaterialUiPickersDate) => {
      field.onChange((data as moment.Moment).format(apiDateFormat));
      setSelectVisibility(false);
    }, []);

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required) {
        return '*required';
      }

      return '';
    }, [error, required]);

    return (
      <>
        <button ref={anchorRef} onClick={pickerToggle} className={styles.wrapper}>
          <FormControl fullWidth tabIndex={-1}>
            <Input
              id={id}
              endAdornment={<CalendarIcon className={clsx([styles.inputEndAdornment])} color={colors.secondary.main} />}
              label={label}
              size="medium"
              variant="outlined"
              value={customValue(field.value as string)}
              className={styles.readOnly}
              fullWidth
              disabled={disabled}
              tabIndex={-1}
              inputProps={{ tabIndex: -1 }}
            />

            {helperText && <HelperTxt>{helperText}</HelperTxt>}
          </FormControl>
        </button>

        <Popper open={selectVisible} anchorEl={anchorRef.current} placement="bottom" className={styles.dropdown}>
          <ClickAwayListener onClickAway={handleClose}>
            <div>
              <Paper elevation={16}>
                <button
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  ref={pickerBoxRef}
                  style={{
                    border: 'none',
                    display: 'block',
                    padding: 0,
                    margin: 0,
                    width: '100%',
                    background: 'none',
                    outline: 'none',
                  }}
                >
                  <CalendarPicker
                    onChange={onChange}
                    date={moment((field.value as string) || min)}
                    minDate={moment(min)}
                    maxDate={max ? moment(max) : undefined}
                    renderDay={renderDay}
                  />
                </button>

                {!!renderNote && (
                  <Box pb={2} maxWidth={256} mx="auto">
                    <Box display="flex" alignItems="center">
                      <Box width={16} height={16} borderRadius={8} bgcolor={colors.functionals.info} mr={1} />
                      <Typography component="div">
                        Price is{' '}
                        <Typography component="span" color="secondary" variant="subtitle1">
                          lower
                        </Typography>{' '}
                        than avarage
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <Box width={16} height={16} borderRadius={8} bgcolor={colors.grey120} mr={1} />
                      <Typography component="div">
                        Price is{' '}
                        <Typography component="span" color="secondary" variant="subtitle1">
                          higher
                        </Typography>{' '}
                        than avarage
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </div>
          </ClickAwayListener>
        </Popper>
      </>
    );
  },
);
